<template>
  <el-container class="admin-page">
    <el-header class="admin-page__title">
      授权管理
    </el-header>

    <el-main>
      <el-alert
        v-if="pendingCount > 0"
        :title="`当前有 ${pendingCount} 条试用申请待确认授权`"
        type="warning"
        show-icon
        :closable="false"
        class="pending-alert"
      />
      <div class="admin-page__toolbar">
        <el-input
          v-model="searchQuery"
          placeholder="输入授权码、用户名或公司名称进行搜索"
          :style="{ width: isMobile ? '100%' : '300px' }"
          clearable
          @input="handleSearch"
        >
          <template #prefix>
            <i class="el-icon-search"></i>
          </template>
        </el-input>

        <el-button type="primary" @click="showAddAuthDialog">新增授权</el-button>
      </div>

      <!-- PC端表格视图 -->
      <el-table 
        v-if="!isMobile"
        :data="authData" 
        style="width: 100%"
        stripe
        v-loading="loading"
      >
        <el-table-column prop="name" label="用户名" width="120" />
        <el-table-column prop="company_name" label="公司名称" min-width="180" />
        <el-table-column prop="industry" label="行业" width="100" />
        <el-table-column prop="auth_code" label="授权码" width="200" />
        <el-table-column prop="application_date" label="申请时间" width="180">
          <template #default="{ row }">
            {{ formatDate(row.application_date) }}
          </template>
        </el-table-column>
        <el-table-column prop="expiration_date" label="到期时间" width="180" />
        <el-table-column prop="contact" label="联系方式" width="200" />
        <el-table-column prop="status" label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="getStatusType(row)" size="small">
              {{ getStatusText(row) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150" fixed="right">
          <template #default="{ row }">
            <el-button @click="viewDetails(row)" type="text" size="small">查看</el-button>
            <el-button 
              v-if="row.status !== 'confirmed'" 
              @click="approveAuth(row.id)" 
              type="text" 
              size="small"
            >
              批准
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 移动端卡片视图 -->
      <div v-else class="mobile-cards" v-loading="loading">
        <el-card v-for="row in authData" :key="row.id" class="mobile-card">
          <div class="card-item">
            <span class="label">用户名：</span>
            <span class="value">{{ row.name }}</span>
          </div>
          <div class="card-item">
            <span class="label">公司：</span>
            <span class="value">{{ row.company_name }}</span>
          </div>
          <div class="card-item">
            <span class="label">状态：</span>
            <el-tag :type="getStatusType(row)" size="small">
              {{ getStatusText(row) }}
            </el-tag>
          </div>
          <div class="card-actions">
            <el-button @click="viewDetails(row)" type="primary" size="small">查看详情</el-button>
            <el-button 
              v-if="row.status !== 'confirmed'" 
              @click="approveAuth(row.id)" 
              type="success" 
              size="small"
            >
              批准
            </el-button>
          </div>
        </el-card>
      </div>

      <!-- 分页器 -->
      <el-pagination
        class="admin-page__pager"
        background
        :layout="isMobile ? 'prev, pager, next' : 'total, sizes, prev, pager, next, jumper'"
        :total="totalCount"
        :page-size="pageSize"
        :current-page="currentPage"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </el-main>

    <!-- 授权确认对话框 -->
    <el-dialog
      v-model="dialogVisible"
      title="授权确认"
      :width="isMobile ? '95%' : '30%'"
      :before-close="handleClose"
      class="auth-dialog"
    >
      <el-form :model="form" :label-width="isMobile ? '80px' : '120px'" class="auth-form">
        <el-form-item label="授权时长">
          <el-select 
            v-model="form.licenseDuration" 
            placeholder="请选择授权时长" 
            default-first-option
            :style="isMobile ? 'width: 100%' : ''"
          >
            <el-option label="一星期" value="7" />
            <el-option label="一个月" value="30" />
            <el-option label="三个月" value="90" />
            <el-option label="一年" value="365" />
            <el-option label="永久" value="-1" />
          </el-select>
        </el-form-item>
        <el-form-item label="产品类型">
          <el-radio-group v-model="form.authVersion" default-first-option class="radio-group">
            <el-radio label="Standard">Standard</el-radio>
            <el-radio label="Plus">Plus</el-radio>
            <el-radio label="Enterprise">Enterprise</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false" :size="isMobile ? 'small' : ''">取消</el-button>
          <el-button type="primary" @click="confirmAuth" :size="isMobile ? 'small' : ''">确认</el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 新增授权对话框 -->
    <el-dialog 
      title="新增授权" 
      v-model="addAuthDialogVisible" 
      :width="isMobile ? '95%' : '50%'"
      class="add-auth-dialog"
    >
      <el-form 
        :model="newAuthForm" 
        :label-width="isMobile ? '80px' : '120px'" 
        :rules="rules" 
        ref="newAuthFormRef"
        class="add-auth-form"
      >
        <el-form-item label="用户名称" prop="name">
          <el-input v-model="newAuthForm.name" :size="isMobile ? 'small' : ''"></el-input>
        </el-form-item>
        <el-form-item label="邮箱" prop="email">
          <el-input v-model="newAuthForm.email" :size="isMobile ? 'small' : ''"></el-input>
        </el-form-item>
        <el-form-item label="公司名称" prop="companyName">
          <el-input v-model="newAuthForm.companyName" :size="isMobile ? 'small' : ''"></el-input>
        </el-form-item>
        <el-form-item label="行业" prop="industry">
          <el-select 
            v-model="newAuthForm.industry" 
            placeholder="请选择行业"
            :size="isMobile ? 'small' : ''"
            :style="isMobile ? 'width: 100%' : ''"
          >
            <el-option label="IT" value="IT"></el-option>
            <el-option label="测绘" value="survey"></el-option>
            <el-option label="建筑" value="building"></el-option>
            <el-option label="制造业" value="manufacturing"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="授权时长" prop="licenseDuration">
          <el-select 
            v-model="newAuthForm.licenseDuration" 
            placeholder="请选择授权时长"
            :size="isMobile ? 'small' : ''"
            :style="isMobile ? 'width: 100%' : ''"
          >
            <el-option label="一星期" :value="7"></el-option>
            <el-option label="一个月" :value="30"></el-option>
            <el-option label="三个月" :value="90"></el-option>
            <el-option label="一年" :value="365"></el-option>
            <el-option label="永久" :value="-1"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="产品类型" prop="authVersion">
          <el-radio-group v-model="newAuthForm.authVersion" class="radio-group">
            <el-radio label="Standard">Standard</el-radio>
            <el-radio label="Plus">Plus</el-radio>
            <el-radio label="Enterprise">Enterprise</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="addAuthDialogVisible = false" :size="isMobile ? 'small' : ''">取消</el-button>
          <el-button type="primary" @click="addNewAuth" :size="isMobile ? 'small' : ''">确定</el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 添加查看详情对话框 -->
    <el-dialog
      v-model="detailsDialogVisible"
      title="授权详情"
      :width="isMobile ? '95%' : '50%'"
      class="details-dialog"
    >
      <div class="details-content">
        <div class="detail-item">
          <span class="detail-label">用户名</span>
          <span class="detail-value">{{ selectedRow?.name }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">公司名称</span>
          <span class="detail-value">{{ selectedRow?.company_name }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">行业</span>
          <span class="detail-value">{{ selectedRow?.industry }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">授权码</span>
          <span class="detail-value">{{ selectedRow?.auth_code }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">申请时间</span>
          <span class="detail-value">{{ formatDate(selectedRow?.application_date) }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">到期时间</span>
          <span class="detail-value">{{ selectedRow?.expiration_date }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">联系方式</span>
          <span class="detail-value">{{ selectedRow?.contact }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">状态</span>
          <el-tag :type="getStatusType(selectedRow)">
            {{ getStatusText(selectedRow) }}
          </el-tag>
        </div>
        <div class="detail-item">
          <span class="detail-label">产品类型</span>
          <span class="detail-value">{{ selectedRow?.authVersion }}</span>
        </div>
        <!-- 如果状态是未确认，显示操作按钮 -->
        <div v-if="selectedRow?.status !== 'confirmed'" class="detail-actions">
          <el-button type="success" @click="approveAuth(selectedRow?.id)">批准授权</el-button>
        </div>
      </div>
    </el-dialog>
  </el-container>
</template>

<script setup>
import { ref, onMounted, computed, reactive } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import authApi from '@/js/connect';

// 移动设备检测
const isMobile = computed(() => {
  return window.innerWidth <= 768;
});

// 数据相关的响应式变量
const authData = ref([]);
const loading = ref(false);
const currentPage = ref(1);
const pageSize = ref(10);
const totalCount = ref(0);
const pendingCount = ref(0);
const searchQuery = ref('');

// 对话框相关的响应式变量
const dialogVisible = ref(false);
const addAuthDialogVisible = ref(false);
const detailsDialogVisible = ref(false);
const selectedRow = ref(null);

// 表单相关的响应式对象
const form = reactive({
  licenseDuration: '',
  authVersion: ''
});

const newAuthForm = reactive({
  name: '',
  email: '',
  companyName: '',
  industry: '',
  licenseDuration: '',
  authVersion: ''
});

// 表单验证规则
const rules = {
  name: [{ required: true, message: '请输入用户名称', trigger: 'blur' }],
  email: [
    { required: true, message: '请输入邮箱', trigger: 'blur' },
    { type: 'email', message: '请输入正确的邮箱格式', trigger: 'blur' }
  ],
  companyName: [{ required: true, message: '请输入公司名称', trigger: 'blur' }],
  industry: [{ required: true, message: '请选择行业', trigger: 'change' }],
  licenseDuration: [{ required: true, message: '请选择授权时长', trigger: 'change' }],
  authVersion: [{ required: true, message: '请选择产品类型', trigger: 'change' }]
};

// 将 formRef 移到外部，和其他 ref 变量一起定义
const newAuthFormRef = ref(null);

// 获取数据方法
const fetchAuthData = async () => {
  loading.value = true;
  try {
    const params = {
      page: currentPage.value,
      per_page: pageSize.value,
      search: searchQuery.value
    };
    const response = await authApi.getAllUsers(params);
    authData.value = response.data.users;
    totalCount.value = response.data.total_count;
    pendingCount.value = response.data.pending_count || 0;
  } catch (error) {
    console.error("获取用户数据失败:", error);
    ElMessage.error('获取用户数据失败，请稍后重试');
  } finally {
    loading.value = false;
  }
};

// 分页方法
const handleSizeChange = (val) => {
  pageSize.value = val;
  fetchAuthData();
};

const handleCurrentChange = (val) => {
  currentPage.value = val;
  fetchAuthData();
};

const handleSearch = () => {
  currentPage.value = 1;
  fetchAuthData();
};

// 查看详情方法
const viewDetails = (row) => {
  selectedRow.value = row;
  detailsDialogVisible.value = true;
};

// 批准授权方法
const approveAuth = async (id) => {
  dialogVisible.value = true;
  selectedRow.value = authData.value.find(item => item.id === id);
};

// 确认授权方法
const confirmAuth = async () => {
  try {
    await authApi.confirmRequest(selectedRow.value.id, form.licenseDuration, form.authVersion);
    ElMessage.success('授权成功');
    dialogVisible.value = false;
    fetchAuthData();
  } catch (error) {
    ElMessage.error('授权失败，请重试');
  }
};

// 新增授权方法
const showAddAuthDialog = () => {
  addAuthDialogVisible.value = true;
};

const addNewAuth = async () => {
  try {
    // 等待表单验证
    await newAuthFormRef.value.validate();
    
    // 构造请求数据
    const authData = {
      name: newAuthForm.name,
      email: newAuthForm.email,
      companyName: newAuthForm.companyName,
      industry: newAuthForm.industry,
      licenseDuration: parseInt(newAuthForm.licenseDuration, 10),
      authVersion: newAuthForm.authVersion
    };

    // 调用 API
    await authApi.addNewAuth(authData);
    
    // 成功处理
    ElMessage.success('授权添加成功');
    addAuthDialogVisible.value = false;
    
    // 重置表单
    newAuthFormRef.value.resetFields();
    
    // 刷新数据
    fetchAuthData();
  } catch (error) {
    if (error.name === 'ValidationError') {
      ElMessage.warning('请填写所有必填项');
    } else {
      console.error('添加授权失败:', error);
      ElMessage.error('添加授权失败，请重试');
    }
  }
};

// 格式化日期方法
const formatDate = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString();
};

// 对话框关闭处理
const handleClose = () => {
  dialogVisible.value = false;
};

// 生命周期钩子
onMounted(() => {
  fetchAuthData();
  window.addEventListener('resize', () => {
    isMobile.value = window.innerWidth <= 768;
  });
});

// 判断授权状态
const isExpired = (row) => {
  if (!row.expiration_date) return false;
  const expirationDate = new Date(row.expiration_date);
  const currentDate = new Date();
  return expirationDate < currentDate;
};

// 获取状态类型
const getStatusType = (row) => {
  if (isExpired(row)) {
    return 'danger';
  } else if (row.status === 'confirmed') {
    return 'success';
  } else {
    return 'warning';
  }
};

// 获取状态文本
const getStatusText = (row) => {
  if (isExpired(row)) {
    return '已过期';
  } else if (row.status === 'confirmed') {
    return '已确认';
  } else {
    return '待确认';
  }
};
</script>

<style scoped>
.pending-alert {
  margin-bottom: 16px;
}

.mobile-cards {
  margin-top: 20px;
  position: relative;
  min-height: 200px;
}

.mobile-card {
  margin-bottom: 15px;
  border-radius: var(--dca-radius);
  background-color: var(--dca-surface);
  border: 1px solid var(--dca-border);
  box-shadow: none;
  transition: transform 0.3s ease;
}

.mobile-card:active {
  transform: scale(0.98);
}

.card-item {
  display: flex;
  padding: 8px 0;
  border-bottom: 1px solid var(--dca-border);
}

.card-item:last-of-type {
  border-bottom: none;
}

.card-item .label {
  width: 80px;
  color: var(--dca-text-muted);
  font-size: 14px;
}

.card-item .value {
  flex: 1;
  color: var(--dca-text);
  font-size: 14px;
  word-break: break-all;
}

.card-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--dca-border);
}

@media screen and (max-width: 768px) {
  .mobile-card {
    margin: 10px 0;
  }

  .card-item {
    padding: 10px 0;
  }
}

/* 详情对话框样式 */
.details-dialog .details-content {
  padding: 20px;
}

.details-dialog .detail-item {
  display: flex;
  padding: 12px 0;
  border-bottom: 1px solid var(--dca-border);
}

.details-dialog .detail-item:last-child {
  border-bottom: none;
}

.details-dialog .detail-label {
  width: 100px;
  color: var(--dca-text-muted);
  font-size: 14px;
}

.details-dialog .detail-value {
  flex: 1;
  color: var(--dca-text);
  font-size: 14px;
  word-break: break-all;
}

.details-dialog .detail-actions {
  margin-top: 20px;
  text-align: right;
}

/* 移动端样式优化 */
@media screen and (max-width: 768px) {
  .details-dialog .detail-item {
    padding: 10px 0;
  }

  .details-dialog .detail-label {
    width: 80px;
    font-size: 13px;
  }

  .details-dialog .detail-value {
    font-size: 13px;
  }
}

/* 对话框通用样式 */
.auth-dialog .auth-form,
.add-auth-dialog .add-auth-form {
  padding: 20px 0;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.radio-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

/* 移动端对话框样式 */
@media screen and (max-width: 768px) {
  .auth-dialog .el-form-item,
  .add-auth-dialog .el-form-item {
    margin-bottom: 15px;
  }

  .auth-dialog .el-select,
  .add-auth-dialog .el-select {
    width: 100%;
  }

  .radio-group {
    flex-direction: column;
    align-items: flex-start;
  }

  .radio-group .el-radio {
    margin-right: 0;
    margin-bottom: 8px;
  }

  .dialog-footer {
    justify-content: space-between;
    padding: 0 10px;
  }

  .dialog-footer .el-button {
    flex: 1;
    margin: 0;
  }
}
</style>